import React, { useState } from "react";
import styled from "styled-components";
import { TestResult } from "../types";
import { SSTFApiClient, getStepJudgment } from "../services/api";
import { isRegressionTest } from "../utils/regressionEvidence";
import { buildTestCaseUrl } from "../utils/testCaseUrl";

interface TestHeaderProps {
  testResult: TestResult;
  onJudgmentUpdate: (testResultId: number, judgmentData: any) => Promise<void>;
  webBaseUrl?: string;
  apiTestResult?: any; // 詳細なAPIデータ
  template?: any;
  projectStatusIds?: any;
  operationLog?: any;
  evidenceFiles?: Record<string, string>;
  regressionBaseline?: Record<string, { full?: string | null; target?: string | null } | null>; // controller が書く marker（1 回目実行でも diff 画像不在でリグレッション判定するのに使う）
  stepJudgments?: { [stepOrder: number]: string }; // ステップごとの判定
  stepInterpretations?: { [stepOrder: number]: boolean }; // ステップごとのAI解釈正誤
  apiClient?: SSTFApiClient | null; // APIクライアントを追加
}

const HeaderContainer = styled.div`
  background-color: #ffffff;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px;
  flex-shrink: 0;
`;

const CommentContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 200px;
  margin-left: 16px;
`;

const CommentLabel = styled.div`
  font-size: 9px;
  color: #6c757d;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 2px;
  text-transform: uppercase;
`;

const CommentText = styled.div`
  font-size: 11px;
  color: #495057;
  line-height: 1.2;
  word-wrap: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-top: 2px;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
  flex-shrink: 1;
`;

const TestTitle = styled.h1`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  max-width: 350px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// リグレッションテスト（マスク適用の差分画像が添付されたテスト）を示すバッジ。
// 判定は evidenceFiles に [ai_generated]diff_step_*.png が存在するかで行う。
// 差分画像は ai_assist の regression_node が生成し、ai_sstf_controller 経由で
// managed_file として test_result に紐付けられる（§3.6 実装）。
const RegressionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background: #7b1fa2;
  border-radius: 10px;
  letter-spacing: 0.02em;
  white-space: nowrap;
`;

// 色分け関数
const getStatusColor = (
  statusId: string | undefined,
  projectStatusIds: any
): string => {
  if (!statusId || !projectStatusIds) return "#9e9e9e";
  if (statusId === projectStatusIds.OK || statusId === projectStatusIds.AI_OK)
    return "#4caf50"; // 緑
  if (
    statusId === projectStatusIds.NG ||
    statusId === projectStatusIds.AI_RESULT_NG ||
    statusId === projectStatusIds.AI_PROCEDURE_NG
  )
    return "#f44336"; // 赤
  if (
    statusId === projectStatusIds.AI_OK_NEED_REVIEW ||
    statusId === projectStatusIds.AI_RESULT_NG_NEED_REVIEW ||
    statusId === projectStatusIds.AI_PROCEDURE_NG_NEED_REVIEW
  ) {
    return "#ff9800"; // オレンジ
  }
  return "#9e9e9e";
};

// API由来のstatusIdを取得
const getApiStatusId = (apiTestResult: any): string | undefined => {
  return apiTestResult?.scriptTestRunResult?.test_result_status_id;
};

// 表示名取得
const getStatusDisplayName = (
  template: any,
  statusId: string | undefined
): string => {
  if (!template || !statusId) return "未実施";
  try {
    const { getStatusDisplayName } = require("../services/api");
    return getStatusDisplayName(template, statusId);
  } catch {
    return statusId;
  }
};

const StatusBadge = styled.span<{ color: string }>`
  padding: 5px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background-color: ${(props) => props.color};
  white-space: nowrap;
  flex-shrink: 0;
`;

const MiddleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: center;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 50px;
`;

const AIJudgmentBadge = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 8px;
  background-color: ${props => props.color}20;
  border: 1px solid ${props => props.color}40;
  flex-shrink: 0;
`;

const AIJudgmentLabel = styled.span`
  font-size: 10px;
  color: #6c757d;
  font-weight: 500;
`;

const AIJudgmentValue = styled.span<{ color: string }>`
  font-size: 11px;
  color: ${props => props.color};
  font-weight: 600;
`;

const InfoLabel = styled.span`
  font-size: 9px;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
`;

const InfoValue = styled.span`
  font-size: 11px;
  color: #2c3e50;
  font-weight: 600;
  margin-top: 1px;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const StatItem = styled.div<{ color?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 40px;
`;

const StatNumber = styled.span<{ color?: string }>`
  font-size: 13px;
  font-weight: 700;
  color: ${(props) => props.color || "#2c3e50"};
`;

const StatLabel = styled.span`
  font-size: 9px;
  color: #6c757d;
  text-transform: uppercase;
  margin-top: 1px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const JudgmentButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background-color: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #e9ecef;
`;

const JudgmentLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-right: 6px;
`;

const JudgmentButton = styled.button<{ variant: 'success' | 'warning' | 'danger'; active?: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
  
  ${props => {
    if (props.variant === 'success') {
      return `
        background: ${props.active ? '#4caf50' : 'white'};
        color: ${props.active ? 'white' : '#4caf50'};
        border-color: #4caf50;
        &:hover {
          background: #4caf50;
          color: white;
        }
      `;
    }
    if (props.variant === 'warning') {
      return `
        background: ${props.active ? '#ff9800' : 'white'};
        color: ${props.active ? 'white' : '#ff9800'};
        border-color: #ff9800;
        &:hover {
          background: #ff9800;
          color: white;
        }
      `;
    }
    if (props.variant === 'danger') {
      return `
        background: ${props.active ? '#f44336' : 'white'};
        color: ${props.active ? 'white' : '#f44336'};
        border-color: #f44336;
        &:hover {
          background: #f44336;
          color: white;
        }
      `;
    }
  }}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


const LinkButton = styled.a`
  padding: 8px;
  border: 1px solid #2196f3;
  border-radius: 6px;
  background: white;
  color: #2196f3;
  font-size: 16px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  
  &:hover {
    background: #f3f8ff;
    transform: translateY(-1px);
    text-decoration: none;
    color: #1976d2;
  }

  &:active {
    transform: translateY(0);
  }
`;

const CopyButton = styled.button`
  padding: 8px;
  border: 1px solid #4caf50;
  border-radius: 6px;
  background: white;
  color: #4caf50;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  
  &:hover {
    background: #f1f8e9;
    transform: translateY(-1px);
    color: #388e3c;
  }

  &:active {
    transform: translateY(0);
  }
  
  &.copied {
    background: #4caf50;
    color: white;
    border-color: #4caf50;
  }
`;

const TestHeader: React.FC<TestHeaderProps> = ({
  testResult,
  onJudgmentUpdate,
  webBaseUrl,
  apiTestResult,
  template,
  projectStatusIds,
  operationLog,
  evidenceFiles,
  regressionBaseline,
  stepJudgments,
  stepInterpretations,
  apiClient,
}) => {
  const [copied, setCopied] = useState(false);
  const [humanJudgment, setHumanJudgment] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // コンポーネント初期化時のデバッグログ
  console.log('=== TestHeader Component Props ===');
  console.log('testResult:', testResult);
  console.log('testResult.value:', testResult.value);
  
  // 既存のタグ分析データを取得
  const existingTagAnalysis = testResult.value?.tag_analysis || {};
  console.log('Existing Tag Analysis on mount:', existingTagAnalysis);
  
  // AI判定を取得（tag_analysis.ai_judgmentから取得）
  const getAIJudgment = () => {
    // tag_analysisに保存されたAI判定を取得
    // これはanalyzerが実行された時に設定される値（"OK"、"手順NG"、"結果NG"の文字列）
    let aiJudgment = existingTagAnalysis.ai_judgment;
    
    // ai_judgmentが空の場合、ステップの判定から全体の判定を推測（後方互換性）
    if (!aiJudgment && existingTagAnalysis.steps && existingTagAnalysis.steps.length > 0) {
      // ステップの中に手順NGまたは結果NGがあるか確認
      const hasStepNG = existingTagAnalysis.steps.some((step: any) => step.ai_judgment === '手順NG');
      const hasResultNG = existingTagAnalysis.steps.some((step: any) => step.ai_judgment === '結果NG');
      
      if (hasStepNG) {
        console.log('ai_judgmentが空のため、ステップから推測: 手順NG');
        return '手順NG';
      } else if (hasResultNG) {
        console.log('ai_judgmentが空のため、ステップから推測: 結果NG');
        return '結果NG';
      } else {
        console.log('ai_judgmentが空のため、ステップから推測: OK');
        return 'OK';
      }
    }
    
    // デバッグ: AI判定取得の詳細を確認
    console.log('=== AI判定取得デバッグ ===');
    console.log('testResult.value?.tag_analysis:', testResult.value?.tag_analysis);
    console.log('existingTagAnalysis:', existingTagAnalysis);
    console.log('tag_analysis.ai_judgment:', aiJudgment);
    
    // AI判定が文字列として正しく取得できているか確認
    if (!aiJudgment) {
      console.log('AI判定欄非表示の理由: ai_judgmentが空');
      return null;
    }
    
    // 既に日本語の判定文字列（"OK"、"手順NG"、"結果NG"）として保存されているので、そのまま返す
    if (aiJudgment === 'OK' || aiJudgment === '手順NG' || aiJudgment === '結果NG') {
      console.log('AI判定を正常に取得:', aiJudgment);
      return aiJudgment;
    }
    
    // 予期しない値の場合はnullを返す
    console.log('予期しないAI判定値:', aiJudgment);
    return null;
  };
  
  const aiJudgment = getAIJudgment();
  console.log('6. 最終的なaiJudgment値:', aiJudgment);
  
  // AI判定の色を取得
  const getAIJudgmentColor = (judgment: string | null): string => {
    if (!judgment) return '#9e9e9e';
    if (judgment === 'OK') return '#4caf50';
    if (judgment === '手順NG') return '#ff9800';
    if (judgment === '結果NG') return '#f44336';
    return '#9e9e9e';
  };
  
  // 初期値として既存の判定を設定
  React.useEffect(() => {
    if (existingTagAnalysis.human_judgment) {
      setHumanJudgment(existingTagAnalysis.human_judgment);
    }
  }, [existingTagAnalysis.human_judgment]);
  
  // 課題クローズ処理を行う関数
  const closeRelatedIssues = async (judgment: string) => {
    console.log('=== 課題クローズ処理開始 ===');
    console.log('judgment:', judgment);
    
    try {
      // APIクライアントが渡されていない場合はエラー
      if (!apiClient) {
        console.error('APIクライアントが設定されていません');
        alert('APIクライアントが設定されていません。設定画面から接続設定を行ってください。');
        return;
      }
      
      console.log('APIクライアント:', apiClient);

      // workspaceIdとprojectIdを取得
      const workspaceId = apiTestResult?.testCaseDetail?.workspace_id;
      const projectId = apiTestResult?.testCaseDetail?.project_id;
      
      console.log('workspaceId:', workspaceId);
      console.log('projectId:', projectId);
      console.log('apiTestResult全体:', apiTestResult);
      console.log('apiTestResult.issues:', apiTestResult?.issues);
      console.log('apiTestResult.scriptTestResultsIssues:', apiTestResult?.scriptTestResultsIssues);
      
      if (!workspaceId || !projectId) {
        console.log('workspace_idまたはproject_idが取得できません');
        console.log('apiTestResult.testCaseDetail:', apiTestResult?.testCaseDetail);
        return;
      }

      // 1. 関連する課題を取得
      const issues = apiTestResult?.issues || [];
      console.log('全課題数:', issues.length);
      console.log('全課題詳細:', JSON.stringify(issues, null, 2));
      
      // 「[AI Generated] 確認の必要なテスト結果があります」の課題を探す
      const targetIssues = issues.filter((issue: any) => {
        console.log('課題名確認:', issue.name);
        return issue.name === '[AI Generated] 確認の必要なテスト結果があります';
      });
      
      console.log('対象課題数:', targetIssues.length);
      console.log('対象課題:', targetIssues);
      
      if (targetIssues.length === 0) {
        console.log('対象となる課題が見つかりません');
        return;
      }

      console.log(`${targetIssues.length}件の課題をクローズします`);

      // 2. 課題テンプレート取得
      console.log('課題テンプレートを取得中...');
      let issueTemplate;
      try {
        issueTemplate = await apiClient.getTemplate('issue', workspaceId, projectId);
        console.log('取得したテンプレート:', issueTemplate);
      } catch (error) {
        console.error('課題テンプレートの取得に失敗:', error);
        alert('課題テンプレートの取得に失敗しました。権限を確認してください。');
        return;
      }
      
      const templateData = JSON.parse(issueTemplate.template);
      console.log('パース後のテンプレートデータ:', templateData);
      
      // 3. issue_statusフィールドを取得
      const issueStatusField = templateData.attributes?.find(
        (attr: any) => attr.id === 'issue_status'
      );
      console.log('issue_statusフィールド:', issueStatusField);
      
      if (!issueStatusField?.data?.options) {
        console.error('課題ステータスオプションが見つかりません');
        console.error('templateData.attributes:', templateData.attributes);
        return;
      }

      // 4. 完了系ステータス（issue_status_type.value = 1）から適切なものを選択
      const completionStatuses = issueStatusField.data.options.filter(
        (opt: any) => opt.issue_status_type?.value === 1
      );
      console.log('完了系ステータス一覧:', completionStatuses);
      
      let targetStatusId: string | undefined;
      
      if (judgment === 'OK' || judgment === '手順NG') {
        // 完了(仕様)を探す
        const targetStatus = completionStatuses.find(
          (opt: any) => opt.display_name?.ja === '完了(仕様)'
        );
        targetStatusId = targetStatus?.value;
        console.log('完了(仕様)を検索:', targetStatus);
      } else if (judgment === '結果NG') {
        // 完了(不具合)を探す
        const targetStatus = completionStatuses.find(
          (opt: any) => opt.display_name?.ja === '完了(不具合)'
        );
        targetStatusId = targetStatus?.value;
        console.log('完了(不具合)を検索:', targetStatus);
      }
      
      if (!targetStatusId) {
        console.error('適切な完了ステータスが見つかりません');
        console.error('judgment:', judgment);
        console.error('利用可能な完了ステータス:', completionStatuses.map((s: any) => s.display_name?.ja));
        return;
      }
      
      console.log('選択された完了ステータスID:', targetStatusId);

      // 5. 未完了系（issue_status_type.value = 0）の課題のみ更新
      for (const issue of targetIssues) {
        // 現在のステータスを確認
        const currentStatus = issueStatusField.data.options.find(
          (opt: any) => opt.value === issue.issue_status_id
        );
        
        console.log(`課題 ${issue.issue_id} の現在のステータス:`, currentStatus);
        console.log(`issue_status_type.value:`, currentStatus?.issue_status_type?.value);
        
        // 未完了系の課題のみ更新
        if (currentStatus?.issue_status_type?.value === 0) {
          try {
            console.log(`課題 ${issue.issue_id} を更新します。ターゲットステータスID: ${targetStatusId}`);
            const updateResult = await apiClient.updateIssueStatus(
              workspaceId,
              projectId,
              issue.issue_id,
              targetStatusId,
              issue  // 既存の課題データを渡す
            );
            console.log(`課題 ${issue.issue_id} をクローズしました。結果:`, updateResult);
          } catch (error) {
            console.error(`課題 ${issue.issue_id} のクローズに失敗:`, error);
          }
        } else {
          console.log(`課題 ${issue.issue_id} は既に完了済みのためスキップ`);
          console.log(`現在のステータス: ${currentStatus?.display_name?.ja}`);
        }
      }
      
      console.log('課題クローズ処理完了');
    } catch (error) {
      console.error('課題クローズ処理でエラー:', error);
      // エラーが発生しても判定保存は継続する
    }
  };

  const handleJudgmentSubmit = async (judgment: string) => {
    console.log('=== handleJudgmentSubmit called ===');
    console.log('Judgment:', judgment);
    console.log('Test Result:', testResult);
    
    if (isUpdating) return;
    
    setIsUpdating(true);
    setHumanJudgment(judgment);
    
    try {
      // 課題クローズ処理を実行（awaitして完了を待つ）
      try {
        await closeRelatedIssues(judgment);
      } catch (closeError) {
        console.error('課題クローズ処理でエラーが発生しましたが、判定保存は続行します:', closeError);
      }
      
      // 既存のtag_analysisからai_judgmentを取得（存在する場合は保持）
      const existingTagAnalysis = testResult.value?.tag_analysis || {};
      
      // AI判定の文字列を取得（既存のものを保持）
      // ai_judgmentはanalyzerが設定した不変の値なので、変更しない
      const aiJudgmentText = existingTagAnalysis.ai_judgment || '';
      
      // 人間の判定文字列を保存（OK/手順NG/結果NG）
      const humanJudgmentText = judgment;
      
      // test_result_status_idに設定するステータスID（OKまたはNGのみ）
      let testResultStatusId: string;
      if (judgment === 'OK') {
        testResultStatusId = projectStatusIds?.OK || 
                            "bf525e4e-24f6-56c2-a365-ded4a2a2beae";
      } else {
        // 手順NG、結果NGの場合はすべてNGのステータスIDを設定
        testResultStatusId = projectStatusIds?.NG ||
                            "458cb677-0708-5af4-b926-bb7d398db6bb";
      }
      
      // 判定が一致するかどうかを判定
      // AI判定（aiJudgmentText）と人間判定（humanJudgmentText）を直接比較
      const isMatch = aiJudgmentText === humanJudgmentText;
      
      // action_tagsのみを使用（primary_item, secondary_item, tertiary_itemは廃止）
      let actionTags: string[] = [];

      // AIと人間の判定が不一致の場合、AIが誤判定したステップのaction_tagsを使用
      if (!isMatch) {
        // 各ステップについて、既存のtag_analysisからAIの判定を取得
        if (existingTagAnalysis.steps && existingTagAnalysis.steps.length > 0) {
          for (let i = 0; i < existingTagAnalysis.steps.length; i++) {
            const stepData = existingTagAnalysis.steps[i];
            const aiStepJudgment = stepData.ai_judgment;

            // AIのステップ判定と人間の全体判定を比較
            // カテゴリが異なる場合、AIの誤判定
            if (aiStepJudgment !== humanJudgmentText) {
              // 保存済みのaction_tagsを使用
              if (stepData.evaluated_tags?.action_tags) {
                actionTags = stepData.evaluated_tags.action_tags;
                break; // 最初のAIエラーステップのみ記録
              }
            }
          }
        }
      }
      
      // 既存のtag_analysisを取得（ai_interpretation_correctを保持するため）
      console.log('=== Before Update ===');
      console.log('testResult.value:', testResult.value);
      const currentTagAnalysis = testResult.value?.tag_analysis || {};
      const existingSteps = currentTagAnalysis.steps || [];
      
      console.log('Current Tag Analysis:', currentTagAnalysis);
      console.log('Existing Steps:', existingSteps);
      console.log('First Step Action Tags:', existingSteps[0]?.evaluated_tags?.action_tags);
      
      // ステップごとの判定データを構築
      const stepsData = testResult.steps.map((step, index) => {
        const stepOrder = index + 1;
        const humanStepJudgment = stepJudgments?.[stepOrder];
        
        // AI判定を取得
        const aiStepJudgment = getStepJudgment(step.statusId);
        
        // 既存のステップデータから ai_interpretation_correct を取得
        // 配列の場合、インデックスで直接アクセスも試みる
        const existingStepData = existingSteps.find((s: any) => s.order === stepOrder || s.step_order === stepOrder) || existingSteps[index];
        
        // ステップレベルの判定一致を確認
        // 人間の判定が明示的に設定されている場合のみ判定一致を計算
        // 未設定の場合、デフォルトでAI判定と同じになるため、一致とみなす
        let stepJudgmentMatch = null;
        if (humanStepJudgment && humanStepJudgment !== '未評価') {
          stepJudgmentMatch = humanStepJudgment === aiStepJudgment;
        } else if (existingStepData?.judgment_match !== undefined) {
          stepJudgmentMatch = existingStepData.judgment_match;
        } else if (!humanStepJudgment || humanStepJudgment === '未評価') {
          // 人間の判定が未設定の場合、デフォルトでAI判定と同じになるので一致
          stepJudgmentMatch = true;
        }
        
        // デバッグログ
        if (index === 0) {
          console.log('=== First Step Debug ===');
          console.log('Step Order:', stepOrder);
          console.log('Index:', index);
          console.log('Existing Steps Array:', existingSteps);
          console.log('Existing Step Data (found):', existingStepData);
          console.log('Human Step Judgment:', humanStepJudgment);
          console.log('AI Step Judgment:', aiStepJudgment);
          console.log('stepJudgments:', stepJudgments);
          console.log('stepInterpretations:', stepInterpretations);
        }
        
        // stepInterpretationsから値を取得（設定されていない場合は既存の値を使用）
        const interpretationFromProps = stepInterpretations?.[stepOrder];
        const existingInterpretation = interpretationFromProps !== undefined ? interpretationFromProps : existingStepData?.ai_interpretation_correct;
        
        // ステップのタグ情報を取得（action_tagsのみ）
        // existingStepDataを使用して既存のaction_tagsを取得・保持
        const stepTags = {
          action_tags: existingStepData?.evaluated_tags?.action_tags || []
        };

        if (index === 0) {
          console.log('Step Tags after merge:', stepTags);
        }
        
        return {
          order: stepOrder,
          evaluated_tags: stepTags,
          ai_judgment: aiStepJudgment,
          human_judgment: humanStepJudgment || existingStepData?.human_judgment || aiStepJudgment,  // 未設定の場合はAI判定をデフォルトとする（仕様通り）
          judgment_match: stepJudgmentMatch,
          // ai_interpretation_correctは既存の値を保持、なければtrue（デフォルトで正しいとみなす）
          // judgment_matchとは独立した概念なので、別々に管理する
          ai_interpretation_correct: existingInterpretation !== undefined ? existingInterpretation : true
        };
      });
      
      // 親レベルのai_interpretation_correctを計算
      // stepsに一つでもfalseがあればfalse、それ以外はtrue（デフォルトで正しいとみなす）
      let parentInterpretationCorrect = true;
      const hasAnyFalse = stepsData.some(step => step.ai_interpretation_correct === false);
      
      if (hasAnyFalse) {
        parentInterpretationCorrect = false;
      }
      
      // タグ分析データを構築（action_tagsのみ）
      const tagAnalysisData = {
        ai_judgment: aiJudgmentText,  // AIの判定文字列を保存（"OK"、"手順NG"、"結果NG"）
        human_judgment: humanJudgmentText,  // 人間の判定文字列（OK/手順NG/結果NG）を保存
        judgment_match: isMatch,
        // ai_interpretation_correctはstepsの値に基づいて設定
        ai_interpretation_correct: parentInterpretationCorrect,
        human_judgment_confirmed: true,
        langfuse_trace_id: existingTagAnalysis.langfuse_trace_id || null,  // LangfuseトレースIDを保持
        evaluated_tags: { action_tags: actionTags },  // action_tagsのみ
        steps: stepsData  // ステップごとの判定を追加
      };
      
      console.log('=== After Update ===');
      console.log('Tag Analysis Data to save:', tagAnalysisData);
      console.log('Parent ai_interpretation_correct:', parentInterpretationCorrect);
      console.log('Steps ai_interpretation_correct values:', stepsData.map(s => s.ai_interpretation_correct));
      console.log('First Step in stepsData:', tagAnalysisData.steps[0]);
      console.log('First Step human_judgment:', tagAnalysisData.steps[0]?.human_judgment);
      console.log('First Step judgment_match:', tagAnalysisData.steps[0]?.judgment_match);
      console.log('First Step ai_interpretation_correct:', tagAnalysisData.steps[0]?.ai_interpretation_correct);
      
      // 更新を実行 - testResultIdとタグ分析データ、およびステータスIDを渡す
      await onJudgmentUpdate(parseInt(testResult.id), {
        tag_analysis: tagAnalysisData,
        test_result_status_id: testResultStatusId  // OKまたはNGのステータスID
      });

      // Langfuseスコア送信（非ブロッキング）
      const langfuseTraceId = existingTagAnalysis.langfuse_trace_id;
      if (langfuseTraceId) {
        const evaluatedSteps = stepsData.filter((s: any) => s.judgment_match !== null);
        const matchRate = evaluatedSteps.length > 0
          ? evaluatedSteps.filter((s: any) => s.judgment_match === true).length / evaluatedSteps.length
          : 1.0;

        const langfuseScores = [
          { name: "human_judgment_match", value: isMatch ? 1 : 0, dataType: "BOOLEAN",
            comment: `AI: ${aiJudgmentText}, Human: ${humanJudgmentText}` },
          { name: "step_judgment_match_rate", value: matchRate, dataType: "NUMERIC",
            comment: `${evaluatedSteps.filter((s: any) => s.judgment_match).length}/${evaluatedSteps.length} ステップ一致` },
          { name: "ai_interpretation_correct", value: parentInterpretationCorrect ? 1 : 0, dataType: "BOOLEAN" },
        ];

        try {
          await fetch("/api/langfuse-scores", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ traceId: langfuseTraceId, scores: langfuseScores }),
          });
        } catch (e) {
          console.warn("Langfuseスコア送信失敗（判定保存は完了済み）:", e);
        }
      }

    } catch (error) {
      console.error('Failed to update judgment:', error);
      alert('判定の保存に失敗しました');
      setHumanJudgment(null);
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDuration = (duration: number): string => {
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const formatTime = (timestamp: number): string => {
    return new Date(timestamp).toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // 現在のテスト結果へのダイレクトURLを生成
  const generateDirectUrl = (): string => {
    if (!apiTestResult) return "";
    const workspaceId = apiTestResult.testCaseDetail?.workspace_id;
    const projectId = apiTestResult.testCaseDetail?.project_id;
    const resultId = apiTestResult.scriptTestRunResult?.result_id || testResult.id;
    
    if (!workspaceId || !projectId || !resultId) return "";
    
    const baseUrl = window.location.origin + window.location.pathname;
    return `${baseUrl}?workspace_id=${workspaceId}&project_id=${projectId}&result_id=${resultId}`;
  };

  // URLをクリップボードにコピー
  const handleCopyUrl = async () => {
    const url = generateDirectUrl();
    if (!url) return;
    
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('URLのコピーに失敗しました:', err);
    }
  };

  const getStepCounts = () => {
    const total = testResult.steps.length;
    const passed = testResult.steps.filter(
      (step) => step.result === "success"
    ).length;
    const warning = testResult.steps.filter(
      (step) => step.result === "warning"
    ).length;
    const failed = testResult.steps.filter(
      (step) => step.result === "failure"
    ).length;
    const withEvidence = testResult.steps.filter(
      (step) => step.evidence
    ).length;

    return { total, passed, warning, failed, withEvidence };
  };

  const stepCounts = getStepCounts();

  // テストケース画面のURL。workspace/project は表示中のテスト結果自身から取る
  // （localStorage の検索条件から取ると tfact_user_request#538 になる）
  const testCaseUrl = buildTestCaseUrl(webBaseUrl, apiTestResult);

  // 証跡・動画カウント用ロジック
  const hasEvidenceForStep = (stepIndex: number): boolean => {
    if (!operationLog || !evidenceFiles) return false;
    const operationStep = operationLog.steps?.[stepIndex];
    if (!operationStep) return false;
    if (operationStep.evidence && evidenceFiles[operationStep.evidence])
      return true;
    return false;
  };

  const getOperationVideos = (): Array<{
    file: string;
    start_time: number;
    url: string;
  }> => {
    if (!operationLog || !operationLog.videos) return [];
    return operationLog.videos
      .map((video: any) => {
        const url = evidenceFiles?.[video.file];
        return url
          ? {
              ...video,
              url: url,
            }
          : null;
      })
      .filter((video: any) => video !== null);
  };

  const evidenceCount = testResult.steps.filter((_, i) =>
    hasEvidenceForStep(i)
  ).length;
  const videoCount = getOperationVideos().length;

  return (
    <HeaderContainer>
      <HeaderContent>
        <LeftSection>
          <StatusBadge
            color={getStatusColor(
              getApiStatusId(apiTestResult),
              projectStatusIds
            )}
          >
            {getStatusDisplayName(template, getApiStatusId(apiTestResult))}
          </StatusBadge>
          {isRegressionTest(evidenceFiles, regressionBaseline) && (
            <RegressionBadge title="リグレッションテスト（マスク適用の差分画像が添付されています）">
              リグレッション
            </RegressionBadge>
          )}
          <TestTitle title={testResult.name}>
            {testResult.metaId && `${testResult.metaId} `}
            {testResult.name}
          </TestTitle>
          {testCaseUrl && (
            <LinkButton
              href={testCaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="テストケース画面を開く"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M10.604 1h4.146a.25.25 0 01.25.25v4.146a.25.25 0 01-.427.177L13.03 4.03 9.28 7.78a.75.75 0 01-1.06-1.06l3.75-3.75-1.543-1.543A.25.25 0 0110.604 1zM3.75 2A1.75 1.75 0 002 3.75v8.5c0 .966.784 1.75 1.75 1.75h8.5A1.75 1.75 0 0014 12.25v-3.5a.75.75 0 00-1.5 0v3.5a.25.25 0 01-.25.25h-8.5a.25.25 0 01-.25-.25v-8.5a.25.25 0 01.25-.25h3.5a.75.75 0 000-1.5h-3.5z"/>
              </svg>
            </LinkButton>
          )}
          {apiTestResult && (
            <CopyButton
              onClick={handleCopyUrl}
              className={copied ? "copied" : ""}
              title={copied ? "コピーしました！" : "この結果のURLをコピー"}
            >
              {copied ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 011.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M0 6.75C0 5.784.784 5 1.75 5h1.5a.75.75 0 010 1.5h-1.5a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-1.5a.75.75 0 011.5 0v1.5A1.75 1.75 0 019.25 16h-7.5A1.75 1.75 0 010 14.25v-7.5z"/>
                  <path d="M5 1.75C5 .784 5.784 0 6.75 0h7.5C15.216 0 16 .784 16 1.75v7.5A1.75 1.75 0 0114.25 11h-7.5A1.75 1.75 0 015 9.25v-7.5zm1.75-.25a.25.25 0 00-.25.25v7.5c0 .138.112.25.25.25h7.5a.25.25 0 00.25-.25v-7.5a.25.25 0 00-.25-.25h-7.5z"/>
                </svg>
              )}
            </CopyButton>
          )}
        </LeftSection>

        <MiddleSection>
          {aiJudgment && (
            <AIJudgmentBadge color={getAIJudgmentColor(aiJudgment)}>
              <AIJudgmentLabel>AI判定:</AIJudgmentLabel>
              <AIJudgmentValue color={getAIJudgmentColor(aiJudgment)}>
                {aiJudgment}
              </AIJudgmentValue>
            </AIJudgmentBadge>
          )}
          
          <InfoItem>
            <InfoLabel>実行時間</InfoLabel>
            <InfoValue>{formatDuration(testResult.executionTime)}</InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel>開始時刻</InfoLabel>
            <InfoValue>{formatTime(testResult.startTime)}</InfoValue>
          </InfoItem>

          <InfoItem>
            <InfoLabel>モード</InfoLabel>
            <InfoValue>{testResult.executionMode || "N/A"}</InfoValue>
          </InfoItem>

          {/* テストケース評価コメント */}
          {testResult.comment && testResult.comment.trim() && (
            <CommentContainer>
              <CommentLabel>
                <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style={{opacity: 0.7}}>
                  <path d="M2.678 11.894a1 1 0 01.287.801 10.97 10.97 0 01-.398 2c1.395-.323 2.247-.697 2.634-.893a1 1 0 01.71-.074A8.06 8.06 0 008 14c3.996 0 7-2.807 7-6 0-3.192-3.004-6-7-6S1 4.808 1 8c0 1.468.617 2.83 1.678 3.894zm-.493 3.905a21.682 21.682 0 01-.713.129c-.2.032-.352-.176-.273-.362a9.68 9.68 0 00.244-.637l.003-.01c.248-.72.45-1.548.524-2.319C.743 11.37 0 9.76 0 8c0-3.866 3.582-7 8-7s8 3.134 8 7-3.582 7-8 7a9.06 9.06 0 01-2.347-.306c-.52.263-1.639.742-3.468 1.105z"/>
                </svg>
                評価
              </CommentLabel>
              <CommentText title={testResult.comment}>
                {testResult.comment}
              </CommentText>
            </CommentContainer>
          )}

          <StatsContainer>
            <StatItem>
              <StatNumber>{stepCounts.total}</StatNumber>
              <StatLabel>総ステップ</StatLabel>
            </StatItem>

            <StatItem>
              <StatNumber color="#4caf50">{stepCounts.passed}</StatNumber>
              <StatLabel>成功</StatLabel>
            </StatItem>

            {stepCounts.warning > 0 && (
              <StatItem>
                <StatNumber color="#ff9800">{stepCounts.warning}</StatNumber>
                <StatLabel>要確認</StatLabel>
              </StatItem>
            )}

            {stepCounts.failed > 0 && (
              <StatItem>
                <StatNumber color="#f44336">{stepCounts.failed}</StatNumber>
                <StatLabel>失敗</StatLabel>
              </StatItem>
            )}

            <StatItem>
              <StatNumber color="#2196f3">{evidenceCount}</StatNumber>
              <StatLabel>証跡</StatLabel>
            </StatItem>

            <StatItem>
              <StatNumber color="#2196f3">{videoCount}</StatNumber>
              <StatLabel>動画</StatLabel>
            </StatItem>
          </StatsContainer>
        </MiddleSection>

        <RightSection>
          <JudgmentButtons>
            <JudgmentLabel>判定</JudgmentLabel>
            <JudgmentButton
              variant="success"
              active={humanJudgment === 'OK'}
              onClick={() => handleJudgmentSubmit('OK')}
              disabled={isUpdating}
            >
              OK
            </JudgmentButton>
            <JudgmentButton
              variant="warning"
              active={humanJudgment === '手順NG'}
              onClick={() => handleJudgmentSubmit('手順NG')}
              disabled={isUpdating}
            >
              手順NG
            </JudgmentButton>
            <JudgmentButton
              variant="danger"
              active={humanJudgment === '結果NG'}
              onClick={() => handleJudgmentSubmit('結果NG')}
              disabled={isUpdating}
            >
              結果NG
            </JudgmentButton>
          </JudgmentButtons>
        </RightSection>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default TestHeader;
